/**
 * PrivacyPolicyPage — bilingual privacy policy for NoJo Enterprise.
 *
 * Owner directive Feb 2026:
 *   - Condensed inline header (no large hero with shield icon eyebrow).
 *   - Match the visible structure of JapanETCcard.com/privacy and
 *     JapanTollReceipts.jp/en/privacy: numbered sections, factual tone.
 *   - DO NOT name specific third-party vendors or technology stack
 *     (no Mandrill, no MongoDB, no Stripe, no AWS, etc.). Refer to them
 *     only as "trusted service providers under NDA".
 *   - APPI (Act on the Protection of Personal Information) framing.
 *
 * Banned wording confirmed not used: "Financial Services", "FSA",
 * "bank-grade", "remittance" — this page is a transparency document.
 */
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Mail } from "lucide-react";

import SEO, { PAGE_SEO } from "@/components/SEO";
import PageSideAura from "@/components/motion/PageSideAura";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

const LAST_UPDATED_EN = "Last updated: February 2026";
const LAST_UPDATED_JA = "最終更新日: 2026年2月";

function formatDate(lang) {
  // JST formatted date, computed at render time so the document always
  // reflects the day it is viewed. We intentionally use the system clock
  // rather than a static literal — the policy is treated as live-managed.
  const d = new Date();
  if (lang === "ja") {
    return new Intl.DateTimeFormat("ja-JP", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "Asia/Tokyo",
    }).format(d);
  }
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "Asia/Tokyo",
  }).format(d);
}

// ─────────────────────────────────────────────────────────────────────────────
// Section copy — consolidated from JEC + JTR privacy policies, normalized to
// the NoJo Enterprise corporate parent. Each section is a tuple of:
//   { heading, paragraphs?: string[], bullets?: string[] }
// Lists ship `bullets`, prose ships `paragraphs`. Both can coexist.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS_EN = [
  {
    heading: "1. Collection of Personal Information",
    sub: [
      {
        title: "1.1 Information You Provide",
        paragraph:
          "In accordance with Japan's Act on the Protection of Personal Information (APPI), we collect only the data necessary to deliver our corporate website and operated services: name, email address, phone number, the contents of any inquiry you submit, and — where you are an active customer of one of our services — the data needed to deliver that specific service (for example: vehicle registration details for ETC card support, account credentials for toll-record delivery).",
      },
      {
        title: "1.2 Automatically Collected Data",
        paragraph:
          "We also collect basic technical information — IP address, browser type, device identifier, access timestamps and referrer — to secure our systems, monitor performance and improve the experience.",
      },
    ],
  },
  {
    heading: "2. Purpose of Use",
    bullets: [
      "Replying to inquiries submitted via the contact form, by email or by telephone.",
      "Operating our services and managing customer accounts, where you have signed up for an operated service.",
      "Sending important notifications about changes to policies or operated services.",
      "Providing customer support in Japanese and English.",
      "Conducting aggregate statistical analyses to improve and customize our services and content.",
      "Detecting and preventing fraud, abuse, or misuse of our systems.",
    ],
  },
  {
    heading: "3. Sharing & Disclosure",
    bullets: [
      "With trusted service providers operating under a non-disclosure agreement (for example: email delivery, hosting, customer support tooling). These providers receive only the data necessary to perform their function and are contractually prohibited from using it for any other purpose.",
      "With official toll-road operators and authorized highway administrators (where applicable to a specific operated service), strictly to fulfill the service you have signed up for.",
      "When required by law or regulation — to comply with legal orders, protect public safety, or prevent fraud.",
      "We never sell, rent, or trade your personal information to third parties.",
    ],
  },
  {
    heading: "4. Use of Cookies",
    paragraph:
      "We use cookies to maintain login sessions on our operated services, to remember your language preference, and to analyze aggregate site traffic. We do not use cookies for advertising or cross-site tracking. You may manage or disable cookies via your browser — note that some features (in particular, signed-in functionality on operated services) may not work without them.",
  },
  {
    heading: "5. Data Storage and Security",
    paragraph:
      "All personal data is protected with industry-standard encryption in transit and at rest, stored on secure servers in Japan, and accessed only by trained staff under access controls. Passwords are processed with advanced one-way hashing. Sensitive identifiers (for example: ETC card numbers) are masked in any customer-facing dashboard.",
  },
  {
    heading: "6. Your Rights under APPI",
    bullets: [
      "Access and review the personal information we hold about you.",
      "Correct or update inaccurate or incomplete data.",
      "Request deletion of your personal information, where permitted by law.",
      "Opt out of any optional communication at any time.",
    ],
    paragraphAfter:
      "To exercise these rights, please contact our support team at the address listed below. We respond within 30 days.",
  },
  {
    heading: "7. Retention of Data",
    paragraph:
      "We retain your information only as long as necessary to deliver the services you have requested, to satisfy legal obligations, or to resolve disputes. When you close an account or request deletion, we remove or anonymize your data within a reasonable period unless retention is required by law.",
  },
  {
    heading: "8. Our Official Domains",
    paragraph:
      "NoJo Enterprise operates this corporate website (www.nojoenterprise.com) together with our operated services on their own dedicated domains. Any website or email claiming to represent NoJo Enterprise on a domain outside the ones we publish on our Corporate Profile page should be treated as suspicious. If in doubt, please contact us directly using the details below to verify before taking action.",
  },
  {
    heading: "9. Updates to This Policy",
    paragraph:
      "We may amend this Privacy Policy to reflect changes in our practices, the services we offer, or the law. Material updates will be posted on this page with a revised \"Last updated\" date, and — where appropriate — communicated to active customers by email. Please review periodically.",
  },
];

const SECTIONS_JA = [
  {
    heading: "1. 個人情報の取得について",
    sub: [
      {
        title: "1.1 お客様から提供いただく情報",
        paragraph:
          "日本の個人情報保護法（APPI）に基づき、当社のコーポレートサイトおよび運営サービスの提供に必要な情報のみを取得します。具体的には、お名前、メールアドレス、電話番号、お問い合わせ内容、また運営サービスをご利用いただいている場合は、そのサービス提供に必要な情報（例：ETCカードサポートの場合は車両登録情報、通行記録配信サービスの場合はアカウント連携に必要な情報など）を取得します。",
      },
      {
        title: "1.2 自動的に取得される情報",
        paragraph:
          "システムのセキュリティ確保、稼働状況の監視、サービス品質の向上のため、IPアドレス、ブラウザの種類、端末識別子、アクセス日時、リファラーなどの基本的な技術情報も取得します。",
      },
    ],
  },
  {
    heading: "2. 利用目的",
    bullets: [
      "お問い合わせフォーム・メール・お電話によるご質問への返信。",
      "運営サービスのご提供および、お客様アカウントの管理（運営サービスをご利用いただいている場合）。",
      "ポリシー・サービス変更に関する重要なお知らせの送信。",
      "日本語・英語でのカスタマーサポート。",
      "サービスおよびコンテンツの改善・最適化を目的とした、集計形式の統計分析。",
      "不正利用・濫用・なりすましの可能性を確認し、防止するため。",
    ],
  },
  {
    heading: "3. 第三者提供および開示",
    bullets: [
      "秘密保持契約に基づく信頼できる委託先（例：メール配信、ホスティング、サポート業務支援など）への提供。これら委託先には業務遂行に必要な情報のみを提供し、契約により他の目的での使用を禁止しています。",
      "運営サービスの内容に応じて、正規の道路事業者・高速道路管理者など、お客様がお申込みいただいたサービスの提供に必要な範囲での共有。",
      "法令に基づく場合、公共の安全のため、または不正防止のために必要な場合の開示。",
      "個人情報を第三者に販売・賃貸・取引することは一切行いません。",
    ],
  },
  {
    heading: "4. クッキーの利用について",
    paragraph:
      "運営サービスにおけるログイン状態の維持、表示言語の記憶、サイト全体のアクセス傾向の集計分析のためにクッキーを使用します。広告配信やサイトをまたいだトラッキングには使用しません。クッキーの管理・無効化はブラウザの設定から行うことができますが、ログインを必要とする一部機能はクッキーがないと正常に動作しない場合があります。",
  },
  {
    heading: "5. 情報の保管とセキュリティ",
    paragraph:
      "すべての個人情報は、通信時・保管時のいずれにおいても業界標準の暗号化により保護され、日本国内の安全なサーバーに保管されています。アクセスは、アクセス制御下で訓練を受けたスタッフのみに限定されています。パスワードは強固な一方向ハッシュ化を施したうえで処理し、ETCカード番号など機微な識別情報は、お客様向けの画面上で必ずマスキング表示しています。",
  },
  {
    heading: "6. 個人情報保護法に基づくお客様の権利",
    bullets: [
      "当社が保有するお客様の個人情報の開示を請求できます。",
      "不正確または不完全な情報の訂正・更新を請求できます。",
      "法令で認められる範囲で、個人情報の削除を請求できます。",
      "任意配信のお知らせは、いつでも配信停止できます。",
    ],
    paragraphAfter:
      "これらの権利を行使される場合は、本ページ末尾のサポート窓口までご連絡ください。30日以内にご対応いたします。",
  },
  {
    heading: "7. 保有期間",
    paragraph:
      "ご利用いただいたサービスの提供、法令上の義務の履行、または紛争解決のために必要な期間に限り、お客様の情報を保有します。アカウント解約または削除請求をいただいた場合、法令で保管が義務付けられている場合を除き、合理的な期間内にデータを削除または匿名化いたします。",
  },
  {
    heading: "8. 公式ドメインについて",
    paragraph:
      "NoJo Enterpriseは、本コーポレートサイト（www.nojoenterprise.com）とともに、各運営サービスを専用のドメイン上で運用しています。「会社概要」ページに掲載しているドメイン以外で、NoJo Enterpriseを名乗るウェブサイトやメールが届いた場合は、なりすましの可能性があります。ご不明な点があれば、本ページ末尾の連絡先より、直接当社までお問い合わせください。",
  },
  {
    heading: "9. 本ポリシーの改定",
    paragraph:
      "本プライバシーポリシーは、運用、提供サービス、関連法令の変更等を反映するため、随時改定する場合があります。重要な変更があった場合は、本ページ上に最終更新日を更新して掲示するほか、必要に応じて運営サービスのご登録メールアドレス宛にもご案内いたします。定期的にご確認いただくことをおすすめいたします。",
  },
];

// ─────────────────────────────────────────────────────────────────────────────

export default function PrivacyPolicyPage() {
  const { lang = "en" } = useParams();
  const isJa = lang === "ja";
  const sections = isJa ? SECTIONS_JA : SECTIONS_EN;

  return (
    <article data-testid="privacy-policy-page" className="bg-white">
      <SEO {...PAGE_SEO.privacy[lang]} path={`/${lang}/legal/privacy`} lang={lang} />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "プライバシーポリシー" : "Privacy Policy", url: `/${lang}/legal/privacy` },
        ])}
      />

      {/* Condensed inline header — one tight line on desktop. No oversized
          hero, no shield-icon eyebrow. */}
      <section className="border-b border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-4 md:py-5">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1
              data-testid="privacy-page-h1"
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-2xl md:text-[30px] font-bold tracking-tight leading-[1.1]",
              ].join(" ")}
            >
              {isJa ? "プライバシーポリシー" : "Privacy Policy"}
            </h1>
            <span className="text-[#CBD5E1]">·</span>
            <span className="text-[12.5px] tracking-[0.04em] text-[#94A3B8] font-mono-display">
              {isJa
                ? `最終更新日: ${formatDate("ja")} (JST)`
                : `Last updated: ${formatDate("en")} (JST)`}
            </span>
          </div>
        </div>
      </section>

      {/* Body — single column, numbered sections, prose + bulleted lists. */}
      <section className="relative isolate">
        <PageSideAura testId="privacy-body-aura" />
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-8 md:py-10 space-y-8 md:space-y-10">
          {sections.map((s, i) => (
            <div key={i} data-testid={`privacy-section-${i}`}>
              <h2
                className={[
                  isJa ? "font-ja" : "font-display",
                  "text-[#0F1D2D] text-[18px] md:text-[20px] font-bold tracking-tight leading-snug mb-3",
                ].join(" ")}
              >
                {s.heading}
              </h2>

              {s.paragraph && (
                <p className="text-[14.5px] leading-[1.85] text-[#334155]">{s.paragraph}</p>
              )}

              {s.bullets && (
                <ul className="space-y-2.5 text-[14.5px] leading-[1.85] text-[#334155] mt-2">
                  {s.bullets.map((line, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <span
                        aria-hidden
                        className="mt-2 h-1.5 w-1.5 rounded-full bg-[#5B2E83] shrink-0"
                      />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              )}

              {s.paragraphAfter && (
                <p className="mt-4 text-[14.5px] leading-[1.85] text-[#334155]">
                  {s.paragraphAfter}
                </p>
              )}

              {s.sub && (
                <div className="space-y-5 mt-2">
                  {s.sub.map((sub, k) => (
                    <div key={k}>
                      <h3
                        className={[
                          isJa ? "font-ja" : "font-display",
                          "text-[#0F1D2D] text-[15.5px] md:text-[16.5px] font-bold tracking-tight leading-snug mb-2",
                        ].join(" ")}
                      >
                        {sub.title}
                      </h3>
                      <p className="text-[14.5px] leading-[1.85] text-[#334155]">
                        {sub.paragraph}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Contact block — concise, in the same flow as the JEC / JTR pages. */}
      <section className="border-t border-[#E2E8F0] bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-8 md:py-10">
          <h2
            className={[
              isJa ? "font-ja" : "font-display",
              "text-[#0F1D2D] text-[18px] md:text-[20px] font-bold tracking-tight leading-snug mb-3",
            ].join(" ")}
          >
            {isJa ? "10. お問い合わせ" : "10. Contact"}
          </h2>
          <p className="text-[14.5px] leading-[1.85] text-[#334155] mb-4">
            {isJa
              ? "本プライバシーポリシー、または個人情報保護法に基づくご自身の権利の行使に関するお問い合わせは、下記の窓口までご連絡ください。"
              : "For questions about this Privacy Policy, or to exercise your rights under Japan's Act on the Protection of Personal Information, please contact us using the details below."}
          </p>
          <ul className="space-y-1.5 text-[14px] leading-[1.7] text-[#334155]">
            <li>
              <span className="font-mono-display text-[12px] tracking-[0.18em] uppercase text-[#94A3B8] mr-2">
                {isJa ? "メール" : "Email"}
              </span>
              <a
                href="mailto:support@nojoenterprise.com?subject=Privacy%20inquiry"
                className="text-[#0E5FB0] hover:underline"
                data-testid="privacy-email-link"
              >
                support@nojoenterprise.com
              </a>
            </li>
            <li>
              <span className="font-mono-display text-[12px] tracking-[0.18em] uppercase text-[#94A3B8] mr-2">
                {isJa ? "電話" : "Phone"}
              </span>
              <a
                href="tel:+81468747156"
                className="text-[#0E5FB0] hover:underline"
                data-testid="privacy-phone-link"
              >
                046-874-7156
              </a>
            </li>
            <li>
              <span className="font-mono-display text-[12px] tracking-[0.18em] uppercase text-[#94A3B8] mr-2">
                {isJa ? "所在地" : "Office"}
              </span>
              {isJa
                ? "〒238-0041 神奈川県横須賀市本町 2-2-1 髙村ビル #202"
                : "#202 Takamura Building, 2-2-1 Honcho, Yokosuka 238-0041, Kanagawa, Japan"}
            </li>
          </ul>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/${lang}/contact`}
              data-testid="privacy-contact-cta"
              className="inline-flex items-center gap-2 bg-[#5B2E83] text-white px-5 py-3 rounded-full text-[13px] font-semibold tracking-tight hover:bg-[#4A2370] transition-colors"
            >
              {isJa ? "お問い合わせフォーム" : "Use the contact form"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </Link>
            <a
              href="mailto:support@nojoenterprise.com?subject=Privacy%20inquiry"
              data-testid="privacy-mailto-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full ring-1 ring-[#E2E8F0] bg-white text-[#0F1D2D] text-[13px] font-semibold tracking-tight hover:bg-[#F8F9FA] transition-colors"
            >
              <Mail className="h-3.5 w-3.5" strokeWidth={2.25} />
              support@nojoenterprise.com
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </a>
          </div>
        </div>
      </section>
    </article>
  );
}
